import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Collapse,
  Dropdown,
  Menu,
  Modal,
  notification,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import Form from 'formik-antd/lib/form';
import Input from 'formik-antd/lib/input';
import Select from 'formik-antd/lib/select';
import Switch from 'formik-antd/lib/switch';
import SubmitButton from 'jsx/common/SubmitButton';
import {
  CustomField,
  Divider,
  Flex,
  FormGroup,
  Grid,
  insertArrayIf,
  openNotificationWithIcon,
  PermissionGuard,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import { Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { MoreVertical, Plus, Trash } from 'react-feather';
import getDeepObjectValue from 'lodash/get';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Api } from '../../services/Api';
import {
  emptyCustomField,
  fieldTypeOptions,
  isIPAddress,
  isNumeric,
  modelTypes,
  StyledCompactTable,
} from '../../util/utils';
import { OptionError } from '../../components/OptionError';
import { AddChoiceButton } from '../../components/AddChoiceButton';
import { DeleteContainer } from '../../components/DeleteContainer';

const { Title, Text } = Typography;

export default function CustomFieldModal() {
  const [t] = useTranslation(['configuration', 'common', 'authorization']);

  const [{ data: configuration }] = useFetch({
    url: '/api/configuration/',
    defaultValue: [],
  });

  const [customFields, setCustomFields] = useState<CustomField[] | []>([]);
  const [isPickList, setPickList] = useState(true);
  const [hasPermission] = useUserinfo();

  const [, fetchCustomFields] = useFetch({
    url: '/api/custom-field/',
    defaultValue: [],
    onSucces: ({ data }) => setCustomFields(data),
    onEmpty: ({ data }) => setCustomFields(data),
  });

  const [modelType, setModeType] = useState('');

  const handleRequiredFieldChange = async (
    e: CheckboxChangeEvent,
    customField: CustomField
  ) => {
    customField.required = !customField.required;
    const resp = await Api.toggle(customField);
    if (resp.status >= 200 && resp.status < 300) {
      fetchCustomFields();
      openNotificationWithIcon(
        notification.success,
        t(`Custom field changed`),
        '',
        5
      );
    } else {
      openNotificationWithIcon(
        notification.error,
        t('Changing custom field failed'),
        t('The custom field could not be changed. Please try again later.')
      );
    }
  };

  const deleteCustomField = async (customField: CustomField) =>
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: (
        <div>
          <blockquote>{customField.name}</blockquote>
          <p>{t("You're about to delete this custom field.")}</p>
        </div>
      ),
      onOk: async () => {
        const resp = await Api.delete(customField.id);
        if (resp.status >= 200 && resp.status < 300) {
          fetchCustomFields();
          openNotificationWithIcon(
            notification.success,
            t(`Custom field deleted`),
            '',
            5
          );
        } else {
          openNotificationWithIcon(
            notification.error,
            t('Deleting custom field failed'),
            t('The custom field could not be deleted. Please try again later.')
          );
        }
      },
      okButtonProps: { danger: true },
      okText: t('OK'),
      cancelText: t('Cancel'),
    });

  const [showModal, setShowModal] = useState<CustomField | null>(null);
  const [showPickList, setShowPicklist] = useState(false);
  const [error, setError] = useState<string>('');

  const resetEmptyField = () => {
    emptyCustomField.id = '';
    emptyCustomField.choices = [];
    emptyCustomField.created_at = '';
    emptyCustomField.updated_at = '';
    emptyCustomField.deleted = false;
    emptyCustomField.center_synced_at = '';
    emptyCustomField.model = '';
    emptyCustomField.field_type = '';
    emptyCustomField.name = '';
    emptyCustomField.description = '';
    emptyCustomField.slug = '';
    emptyCustomField.required = false;
    emptyCustomField.site = '';
    emptyCustomField.placeholder = '';
  };

  const handleAddFieldClick = (model: any) => {
    resetEmptyField();
    setError('');
    setModeType(model.name);
    setShowPicklist(false);
    setShowModal(emptyCustomField);
  };

  const editCustomField = (customField: CustomField) => {
    resetEmptyField();
    setError('');
    setFieldTypeInput(customField.field_type);
    setModeType(
      modelTypes.find(p => p.model === customField.model)?.name ?? ''
    );
    setShowPicklist(customField.choices.length > 0);
    setShowModal(customField);
  };
  const closeModal = () => {
    setShowModal(null);
  };

  const postCustomField = async (customField: CustomField, action: string) => {
    const resp =
      action === 'post'
        ? await Api.post(customField)
        : await Api.put(customField);
    if (resp.status >= 200 && resp.status < 300) {
      fetchCustomFields();
      openNotificationWithIcon(
        notification.success,
        action === 'post' ? t(`Custom field added`) : t(`Custom field changed`),
        '',
        5
      );
    } else {
      openNotificationWithIcon(
        notification.error,
        action === 'post'
          ? t('Adding custom field failed')
          : t('Changing custom field failed'),
        action === 'post'
          ? t('The custom field could not be added. Please try again later.')
          : t('The custom field could not be changed. Please try again later.')
      );
    }
  };

  const [choiceInput, setChoiceInput] = useState('');
  const [fieldTypeInput, setFieldTypeInput] = useState('');

  const handleAddChoice = () => {
    if (showModal) {
      if (choiceInput) {
        if (validateChoiceInput()) {
          if (showModal.choices.indexOf(choiceInput) >= 0) {
            setError(t('Duplicate item'));
          } else {
            showModal.choices.push(choiceInput);
            setChoiceInput('');
          }
        }
      }
    }
  };
  const validateChoiceInput = () => {
    if (showModal) {
      if (!fieldTypeInput) {
        setError(t('Please select a field type first'));
        return false;
      } else {
        if (!fieldTypeInput) {
          setError(t('Please enter one option first'));
          return false;
        }
        if (fieldTypeInput === 'integer' && !isNumeric(choiceInput)) {
          setError(t('Please enter an integer value'));
          return false;
        }
        if (
          fieldTypeInput === 'boolean' &&
          choiceInput !== 'true' &&
          choiceInput !== 'false'
        ) {
          setError(t('Please enter a boolean value'));
          return false;
        }
        if (fieldTypeInput === 'ip_address' && !isIPAddress(choiceInput)) {
          setError(t('Please enter a valid IP address'));
          return false;
        }
      }
    }
    return true;
  };
  const handleRemoveChoice = (choice: any) => {
    if (showModal) {
      setShowModal(showModal => {
        return {
          ...showModal,
          choices: showModal
            ? showModal.choices.filter(item => item !== choice)
            : [],
        } as CustomField;
      });
    }
  };
  const handleSubmit = async (values: any) => {
    try {
      let action = 'put';
      if (showModal) {
        values.choices = showPickList ? showModal.choices : [];
      }
      if (!values.model) {
        const model = modelTypes.filter(p => p.name === modelType);
        if (model) {
          values.model = model[0].model;
        }
        values = {
          model: values.model,
          field_type: values.field_type,
          name: values.name,
          required: values.required,
          choices: showPickList ? values.choices : [],
          description: values.description,
          placeholder:
            values.field_type !== 'boolean' ? values.placeholder : '',
        };
        action = 'post';
      }
      postCustomField(values, action);
      setShowModal(null);
    } catch (e: any) {
      openNotificationWithIcon(
        notification.error,
        t('Custom field not saved'),
        <small>{e.message}</small>
      );
    }
  };

  const handlePickListChange = (value: any) => {
    setShowPicklist(value);
  };
  const resetPickList = () => {
    if (showModal) {
      showModal.choices = [];
    }
    setShowPicklist(false);
  };

  return (
    <>
      <Title level={4}>{t('Asset Configuration')}</Title>
      <Collapse defaultActiveKey={[emptyCustomField.id]} accordion>
        {getDeepObjectValue(configuration, 'asset_types', []).map(
          (assetType: any, index: number) => {
            return (
              <Collapse.Panel
                header={t(
                  modelTypes.find(m => m.model === assetType.model)
                    ?.display_name ?? ''
                )}
                key={`asset${index}`}
                forceRender
              >
                <Flex h="between" v="center" className="mb-2.5">
                  <Text type="secondary">{t('Fields')}</Text>
                  <PermissionGuard codename={'add_customfield'}>
                    <Button
                      className="text-xs bg-black/10 h-5 pt-px"
                      shape="round"
                      icon={<Plus size={10} />}
                      onClick={() => handleAddFieldClick(assetType)}
                      data-ta={`${assetType.model}-AddCustomFieldButton`}
                    >
                      {t('Add field')}
                    </Button>
                  </PermissionGuard>
                </Flex>
                <StyledCompactTable
                  bordered={false}
                  dataSource={
                    customFields
                      .filter(
                        (f: any) => f.model === assetType.model && !f.deleted
                      )
                      .sort((a, b) => (a.created_at > b.created_at ? -1 : 1)) ||
                    []
                  }
                  rowKey="id"
                  pagination={false}
                  columns={[
                    {
                      title: t('Name'),
                      dataIndex: 'name',
                      key: 'name',
                    },
                    {
                      title: t('Description'),
                      dataIndex: 'description',
                      key: 'description',
                    },
                    {
                      title: t('Type'),
                      dataIndex: 'field_type',
                      key: 'field_type',
                    },
                    {
                      title: t('Required'),
                      dataIndex: 'required',
                      key: 'required',
                      render: (required: any, record: any) => {
                        return (
                          <Checkbox
                            disabled={!hasPermission('change_customfield')}
                            checked={required}
                            onChange={e => handleRequiredFieldChange(e, record)}
                          />
                        );
                      },
                    },
                    ...insertArrayIf(
                      hasPermission([
                        'change_customfield',
                        'delete_customfield',
                      ]),
                      [
                        {
                          title: '',
                          key: 'action',
                          render: (_: any, record: any) => (
                            <Dropdown.Button
                              // @ts-ignore
                              type="text"
                              icon={<MoreVertical size={14} />}
                              overlay={
                                <Menu>
                                  <Menu.Item
                                    hidden={
                                      !hasPermission('change_customfield')
                                    }
                                    key="1"
                                    onClick={() => editCustomField(record)}
                                  >
                                    {t('Edit')}
                                  </Menu.Item>
                                  <Menu.Item
                                    hidden={
                                      !hasPermission('delete_customfield')
                                    }
                                    key="2"
                                    onClick={() => deleteCustomField(record)}
                                  >
                                    {t('Delete')}
                                  </Menu.Item>
                                </Menu>
                              }
                            />
                          ),
                        },
                      ]
                    ),
                  ]}
                ></StyledCompactTable>
              </Collapse.Panel>
            );
          }
        )}
      </Collapse>
      <Modal
        onCancel={closeModal}
        open={!!showModal}
        destroyOnClose
        title={
          showModal?.id
            ? t(
                `Edit field of ${
                  modelTypes.filter(p => p.name === modelType)[0]?.display_name
                }s`
              )
            : t(
                `Add field to ${
                  modelTypes.filter(p => p.name === modelType)[0]?.display_name
                }s`
              )
        }
        width={900}
        footer={null}
      >
        {showModal ? (
          <Formik initialValues={showModal} onSubmit={handleSubmit}>
            {({ values }) => (
              <Form>
                <Grid.Row>
                  <Grid.Col width={12}>
                    <FormGroup
                      required
                      label={t('Name')}
                      input={<Input required name="name" />}
                    />
                  </Grid.Col>
                  <Grid.Col width={12}>
                    <FormGroup
                      label={t('Placeholder')}
                      input={
                        <Input
                          name="placeholder"
                          disabled={values.field_type === 'boolean'}
                        />
                      }
                    />
                  </Grid.Col>
                  <Grid.Col width={12}>
                    <FormGroup
                      label={t('Description')}
                      input={<Input.TextArea name="description" />}
                    />
                  </Grid.Col>
                  <Grid.Col width={12}>
                    <FormGroup
                      required
                      label={t('Type')}
                      input={
                        <Select
                          name="field_type"
                          disabled={!!showModal?.id}
                          style={{ width: '100%' }}
                          onChange={item => {
                            setFieldTypeInput(item);
                            setPickList(
                              !['user_group', 'boolean', 'date_time'].includes(
                                item
                              )
                            );
                            resetPickList();
                          }}
                        >
                          {fieldTypeOptions(t).map(item => (
                            <Select.Option key={item.key} value={item.value}>
                              {item.key}
                            </Select.Option>
                          ))}
                        </Select>
                      }
                    />
                  </Grid.Col>
                  <Grid.Col width={12}>
                    <FormGroup
                      label={t('Required')}
                      input={
                        <Switch name="required" data-ta="RequiredButton" />
                      }
                    />
                  </Grid.Col>
                  <Grid.Col width={12}>
                    <FormGroup
                      label={t('Pick List')}
                      input={
                        <Switch
                          name="picklist"
                          data-ta="PickListButton"
                          disabled={
                            !isPickList ||
                            ['user_group', 'boolean', 'date_time'].includes(
                              showModal.field_type
                            )
                          }
                          onChange={e => handlePickListChange(e)}
                          checked={showPickList}
                        />
                      }
                    />
                  </Grid.Col>
                </Grid.Row>
                {showPickList ? (
                  <Grid.Row>
                    <Grid.Col width={12}>
                      <FormGroup
                        label={t('Options')}
                        input={
                          <Input
                            name="choice"
                            value={choiceInput}
                            onChange={e => {
                              setError('');
                              setChoiceInput(e.target.value);
                            }}
                            onBlur={() => setError('')}
                            maxLength={50}
                          />
                        }
                      />
                      <OptionError id="optionError">{error}</OptionError>
                    </Grid.Col>
                    <Grid.Col width={12}>
                      <table
                        style={{
                          marginBottom: '10px',
                        }}
                      >
                        <tbody>
                          {showModal.choices.map((choice, index) => {
                            return (
                              <tr key={`tr${index}`}>
                                <td
                                  key={`tdb${index}`}
                                  style={{ paddingLeft: '5px' }}
                                >
                                  • {choice}
                                </td>
                                <td
                                  key={`tdv${index}`}
                                  style={{ paddingLeft: '5px' }}
                                >
                                  <DeleteContainer
                                    key={`dc${index}`}
                                    onClick={() => handleRemoveChoice(choice)}
                                  >
                                    <Trash size={12} color="red" />
                                  </DeleteContainer>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Grid.Col>
                    <Grid.Col width={12}>
                      <AddChoiceButton type="button" onClick={handleAddChoice}>
                        <Plus color="#96A9A7" /> {t('Add option')}
                      </AddChoiceButton>
                    </Grid.Col>
                  </Grid.Row>
                ) : (
                  <></>
                )}
                <Divider />
                <Grid.Row>
                  <Grid.Col>
                    <Flex h="end">
                      <SubmitButton
                        disabled={!values.field_type || !values.name}
                      >
                        {t('Submit')}
                      </SubmitButton>
                    </Flex>
                  </Grid.Col>
                </Grid.Row>
              </Form>
            )}
          </Formik>
        ) : (
          <></>
        )}
      </Modal>
    </>
  );
}
